import React, { useCallback, useContext, useMemo } from 'react'
import { dateTimeCol, intCol, numCol } from '@integec/grid-tools/lib/cols'
import CommonScreen from 'admin/component/CommonScreen'
import { Button, DatePicker } from 'antd'
import * as ScreenPermissions from 'core/EventActions'
import { hasAccesPermits, isValidYearRange } from 'core/utils'
import { getApiTargetInfo, getEnvConfig, getPermitActions } from 'core'
import {
  getVerificationHourlyMwAdmin,
  importVerificationHourlyMwAdmin,
  processVerificationHourlyMwAdmin,
} from '../api/pgads'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getValidYearRange,
} from 'core/selectors'
import {
  ErrorContext,
  SpinContext,
  JcTmpErrorContext,
  DatePickerContext,
} from 'context'
import { connect } from 'react-redux'

const VerificationHourly = ({
  targetInfo,
  envConfig,
  actionPermits,
  ...props
}) => {
  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    DatePickerContext
  )
  const { setError } = useContext(ErrorContext)
  const { setSpinning } = useContext(SpinContext)
  const { setTmpError } = useContext(JcTmpErrorContext)

  const screenCriteria = (s, p, su, onChange) => {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '250px',
          columnGap: '5px',
          // width: '100%',
        }}
      >
        <DatePicker.RangePicker
          style={{ marginLeft: '15px' }}
          allowClear={false}
          format="MM/DD/YYYY"
          value={selectedDateRangePicker || []}
          onChange={d => {
            setSelectedDateRagePicker(d)
            onChange()
            // retrieve({ dates: d })
          }}
        />
      </div>
    )
  }

  const withCriteria = _ => ({
    // unit: unitSelection[1],
    start: selectedDateRangePicker[0],
    end: selectedDateRangePicker[1],
  })

  const isValidDateRange = useCallback(
    _ =>
      isValidYearRange(
        props.validYearRange,
        selectedDateRangePicker[0],
        selectedDateRangePicker[1]
      ),
    [props.validYearRange, selectedDateRangePicker]
  )

  const importData = _ => {
    if (!isValidDateRange()) {
      return
    }
    setSpinning(true)
    importVerificationHourlyMwAdmin(targetInfo)(withCriteria()).fork(
      setError,
      tmpError => {
        setSpinning(false)
        setTmpError(tmpError)
      }
    )
  }
  const processData = _ => {
    if (!isValidDateRange()) {
      return
    }
    setSpinning(true)
    processVerificationHourlyMwAdmin(targetInfo)(withCriteria()).fork(
      setError,
      tmpError => {
        setSpinning(false)
        setTmpError(tmpError)
      }
    )
  }

  const importPermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.CHANGE_PERMISSIONS],
        matchAny: true,
      }),
    [actionPermits, envConfig.authGodMode]
  )

  const extraCriteriaAfterButton = _ => {
    if (!importPermission) return <div />
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '10px',
          marginLeft: '10px',
        }}
      >
        <Button icon="import" type="primary" onClick={importData}>
          Import
        </Button>
        <Button type="danger" onClick={processData} icon="thunderbolt">
          Process
        </Button>
      </div>
    )
  }

  return (
    <CommonScreen
      beforeRetrieveValidation={isValidDateRange}
      extraCriteriaAfterButton={extraCriteriaAfterButton}
      titleProps={{ width: '300px' }}
      screenCriteria={screenCriteria}
      withUnitSelection={false}
      withCriteria={withCriteria}
      retrieveTask={getVerificationHourlyMwAdmin}
      title="Verification Hourly MWH Import"
      exportTitle="Verification Hourly MWH Import "
      header={InputsPnodertvaluesHeaders}
      isAdminScreen={false}
      autoRetrieve={false}
      sortOptions={InputsPnodertvaluesHeaders}
      widthOffset={50}
      insertComp={false}
      insertCompHeight={0}
      onInsertEvent={_ => _}
      allowInsert={false}
      withoutModify
      heightOffset={250}
    />
  )
}

const mapStateToProps = state => ({
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  targetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  validYearRange: getValidYearRange(state),
})

export default connect(mapStateToProps)(VerificationHourly)

const InputsPnodertvaluesHeaders = [
  intCol({
    ident: 'pnodeid',
    display: 'Pnode ID',
    isKey: true,
    sortOrder: 'asc',
  }),
  dateTimeCol({
    ident: 'hour',
    display: 'Hour (GMT)',
    isKey: true,
    sortOrder: 'asc',
    width: 150,
  }),
  numCol({ ident: 'dispatchrate', display: 'Dispatch Rate', isFiltered: true }),
  numCol({ ident: 'econmin', display: 'Econ. Min.', isFiltered: true }),
  numCol({ ident: 'econmax', display: 'Econ. Max.', isFiltered: true }),
  numCol({ ident: 'semwh', display: 'SEMWH', isKey: true }),
  numCol({ ident: 'segenmwh', display: 'SEGENMWH', isFiltered: true }),
  numCol({
    ident: 'isfollowingisodisind',
    display: 'Is Following ISO DISIND',
    isFiltered: true,
    width: 200,
  }),
  numCol({ ident: 'desiredmwh', display: 'DESIREDMWH', isFiltered: true }),
  numCol({
    ident: 'settlementmwh',
    display: 'Settlement MWH',
    isFiltered: true,
  }),
  numCol({
    ident: 'settlementgenmwh',
    display: 'Settlement Gen. MWH',
    isFiltered: true,
    width: 150,
  }),
  numCol({ ident: 'revmtrldadj', display: 'REVMTRLDADJ', isFiltered: true }),
  numCol({ ident: 'neggenadj', display: 'NEGGENADJ', isFiltered: true }),
  numCol({
    ident: 'settlementmwhplusadj',
    display: 'Settlement MWH Plus SADJ',
    isFiltered: true,
    width: 200,
  }),
  numCol({ ident: 'nodalmwh', display: 'NODALMWH', isFiltered: true }),
  numCol({ ident: 'residualmwh', display: 'RESIDUALMWH', isFiltered: true }),
  // numCol({ ident: 'deleted', display: 'Deleted', isKey: true }),
  numCol({
    ident: 'revmtrldadjwithlosses',
    display: 'REVMTRLDADJWITHLOSSES',
    isFiltered: true,
    width: 200,
  }),
  numCol({
    ident: 'settlementmwhplusadjwithlosses',
    display: 'Settlement MWH Plus ADJ. With Losses',
    isFiltered: true,
    width: 200,
  }),
  dateTimeCol({
    ident: 'createddate',
    display: 'Created Date',
    isFiltered: true,
    width: 150,
  }),
  dateTimeCol({
    ident: 'modifiedDate',
    display: 'Modified Date',
    width: 200,
    showInRowEditor: false,
    dataFormat: 'YYYY-MM-DD[T]HH:mm:SS.sssZZ',
    displayFormat: 'MM/DD/YYYY HH:mm:SS',
    type: 'date-time',
    isKey: true,
  }),
]
